import { type PropsWithChildren, useRef } from 'react';

import { createStore, type Store } from '../../../core/store/store.js';
import { INTERACTION_MODE } from '../../core/components/interactions-overlay/types.js';
import { getInitialZoomOut } from '../../core/components/zoom/utils/get-initial-zoom-out.js';
import { initialState } from '../constants.js';
import { TimeseriesChartStateContext } from '../context/TimeseriesChartState.context.js';
import type { TimeseriesChartState } from '../types/state.js';
import type {
  TimeseriesPerVariant,
  TimeseriesZoomDomain,
} from '../types/timeseries.js';
import { getXAxisRange } from '../utils/x-axis.js';

interface StoreProviderProps extends PropsWithChildren {
  data: TimeseriesPerVariant;
  currentDomain?: TimeseriesZoomDomain;
  initialDomain?: TimeseriesZoomDomain;
  infiniteZoom?: boolean;
}

export const StoreProvider = ({
  data,
  currentDomain,
  initialDomain,
  infiniteZoom,
  children,
}: StoreProviderProps) => {
  const storeRef = useRef<Store<TimeseriesChartState>>(undefined);

  const { min: minFromData, max: maxFromData } = getXAxisRange(data);
  const initialDomainRange = initialDomain ?? [minFromData, maxFromData];
  const lastInteraction = storeRef?.current?.getState().interactionMode;

  storeRef.current = createStore<TimeseriesChartState>({
    ...initialState,
    currentDomain: currentDomain as [Date, Date] | [number, number],
    interactionMode: lastInteraction || INTERACTION_MODE.Explore,
    zoom: {
      ...initialState.zoom,
      zoomOutEnabled:
        infiniteZoom || getInitialZoomOut(currentDomain, initialDomainRange),
    },
  });

  return (
    <TimeseriesChartStateContext.Provider value={storeRef.current}>
      {children}
    </TimeseriesChartStateContext.Provider>
  );
};
